/**
 * @fileoverview Patrones UTF-8 adicionales para PTEL
 * @module lib/UTF8PatternsAdicionales
 * @version 1.3.0
 * 
 * AMPLIACIÓN: 61 → 107 patrones (+46 nuevos)
 * 
 * Organización por tiers según frecuencia de aparición:
 * - HOT (>5%): 20 patrones muy frecuentes, procesados primero
 * - WARM (1-5%): 16 patrones moderados + 6 caracteres control
 * - COLD (<1%): 14 patrones raros pero documentados
 * 
 * Fuentes de los nuevos patrones:
 * - Análisis de 44 documentos PTEL reales
 * - Errores OCR en documentos escaneados
 * - Exportaciones gvSIG/AutoCAD
 * - Formatos legacy de administraciones públicas
 * - Corrupción macOS HFS+/NTFS en nombres de archivo
 * - Documento PATRONES_UTF8_ADICIONALES.md (2025-12-22)
 */

// =====================================================
// TIPOS
// =====================================================

export interface UTF8Pattern {
  /** Patrón corrupto a buscar */
  readonly corrupted: string;
  /** Texto correcto de reemplazo */
  readonly correct: string;
  /** Tier de frecuencia */
  readonly tier: 'hot' | 'warm' | 'cold';
  /** Descripción del patrón */
  readonly description: string;
  /** Regex precompilado para búsqueda */
  readonly regex: RegExp;
  /** Frecuencia observada (%) */
  readonly frequency?: number;
  /** Origen del patrón */
  readonly source?: string;
}

export interface PatternStats {
  /** Total de patrones */
  total: number;
  /** Patrones por tier */
  byTier: { hot: number; warm: number; cold: number };
  /** Aplicaciones en esta sesión */
  applications: Map<string, number>;
}

// =====================================================
// PATRONES NUEVOS - TIER HOT (Alta frecuencia >5%)
// =====================================================

/**
 * 20 patrones HOT nuevos - Procesados primero por frecuencia
 * Incluye patrones críticos de nombres de archivo PTEL
 */
export const NEW_HOT_PATTERNS: Readonly<UTF8Pattern[]> = [
  // H01-H05: Vocales acentuadas corruptas en nombres archivo (macOS NFD + Windows NTFS)
  // Patrón: vocal + Ì_ = vocal con tilde corrupta → vocal acentuada
  {
    corrupted: 'aÌ_',
    correct: 'á',
    tier: 'hot',
    description: 'a con tilde corrupta (NFD corruption)',
    regex: /aÌ_/g,
    frequency: 12.5,
    source: 'Nombres archivo ODT',
  },
  {
    corrupted: 'eÌ_',
    correct: 'é',
    tier: 'hot',
    description: 'e con tilde corrupta (NFD corruption)',
    regex: /eÌ_/g,
    frequency: 11.0,
    source: 'Nombres archivo ODT',
  },
  {
    corrupted: 'iÌ_',
    correct: 'í',
    tier: 'hot',
    description: 'i con tilde corrupta (NFD corruption)',
    regex: /iÌ_/g,
    frequency: 10.5,
    source: 'Nombres archivo ODT: Zalabí',
  },
  {
    corrupted: 'oÌ_',
    correct: 'ó',
    tier: 'hot',
    description: 'o con tilde corrupta (NFD corruption)',
    regex: /oÌ_/g,
    frequency: 10.0,
    source: 'Nombres archivo ODT',
  },
  {
    corrupted: 'uÌ_',
    correct: 'ú',
    tier: 'hot',
    description: 'u con tilde corrupta (NFD corruption)',
    regex: /uÌ_/g,
    frequency: 12.0,
    source: 'Nombres archivo ODT: Benalúa, Güéjar',
  },
  {
    corrupted: 'ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢',
    correct: "'",
    tier: 'hot',
    description: 'Apóstrofe con triple corrupción',
    regex: /ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢/g,
    frequency: 8.2,
    source: 'ODT LibreOffice legacy',
  },
  {
    corrupted: 'Ã‚Âº',
    correct: 'º',
    tier: 'hot',
    description: 'Símbolo ordinal masculino',
    regex: /Ã‚Âº/g,
    frequency: 7.5,
    source: 'Numeración calles "1º, 2º"',
  },
  {
    corrupted: 'Ã‚Âª',
    correct: 'ª',
    tier: 'hot',
    description: 'Símbolo ordinal femenino',
    regex: /Ã‚Âª/g,
    frequency: 6.8,
    source: 'Numeración "1ª planta"',
  },
  {
    corrupted: 'Ã‚Â€',
    correct: '€',
    tier: 'hot',
    description: 'Símbolo euro corrupto',
    regex: /Ã‚Â€/g,
    frequency: 5.9,
    source: 'Presupuestos en documentos',
  },
  {
    corrupted: 'â€"',
    correct: '–',
    tier: 'hot',
    description: 'Guion largo (en-dash)',
    regex: /â€"/g,
    frequency: 5.7,
    source: 'Rangos numéricos "2020–2024"',
  },
  {
    corrupted: 'â€"',
    correct: '—',
    tier: 'hot',
    description: 'Guion extra largo (em-dash)',
    regex: /â€"/g,
    frequency: 5.4,
    source: 'Separadores en texto',
  },
  {
    corrupted: 'Ã‚Â·',
    correct: '·',
    tier: 'hot',
    description: 'Punto medio (interpunct)',
    regex: /Ã‚Â·/g,
    frequency: 5.2,
    source: 'Catalán "l·l", separadores',
  },
  {
    corrupted: 'ÃƒÂ¼',
    correct: 'ü',
    tier: 'hot',
    description: 'U con diéresis minúscula',
    regex: /ÃƒÂ¼/g,
    frequency: 5.1,
    source: 'Nombres alemanes, "pingüino"',
  },
  // H14-H20: Doble encoding nivel 2 (triple corrupción)
  // NOTA: Usamos \x27 para escapar comilla simple en patrones con Æ'
  {
    corrupted: 'ÃƒÆ\x27Ã‚Â±',
    correct: 'ñ',
    tier: 'hot',
    description: 'ñ con triple corrupción',
    regex: /ÃƒÆ'Ã‚Â±/g,
    frequency: 6.5,
    source: 'Copiar/pegar Word ↔ LibreOffice',
  },
  {
    corrupted: 'ÃƒÆ\x27Ã‚Â¡',
    correct: 'á',
    tier: 'hot',
    description: 'á con triple corrupción',
    regex: /ÃƒÆ'Ã‚Â¡/g,
    frequency: 6.3,
    source: 'Copiar/pegar multi-aplicación',
  },
  {
    corrupted: 'ÃƒÆ\x27Ã‚Â©',
    correct: 'é',
    tier: 'hot',
    description: 'é con triple corrupción',
    regex: /ÃƒÆ'Ã‚Â©/g,
    frequency: 6.1,
    source: 'Copiar/pegar multi-aplicación',
  },
  {
    corrupted: 'ÃƒÆ\x27Ã‚Â³',
    correct: 'ó',
    tier: 'hot',
    description: 'ó con triple corrupción',
    regex: /ÃƒÆ'Ã‚Â³/g,
    frequency: 5.9,
    source: 'Copiar/pegar multi-aplicación',
  },
  {
    corrupted: 'ÃƒÆ\x27Ã‚Â­',
    correct: 'í',
    tier: 'hot',
    description: 'í con triple corrupción',
    regex: /ÃƒÆ'Ã‚Â­/g,
    frequency: 5.8,
    source: 'Copiar/pegar multi-aplicación',
  },
  {
    corrupted: 'ÃƒÆ\x27Ã‚Âº',
    correct: 'ú',
    tier: 'hot',
    description: 'ú con triple corrupción',
    regex: /ÃƒÆ'Ã‚Âº/g,
    frequency: 5.6,
    source: 'Copiar/pegar multi-aplicación',
  },
  {
    corrupted: 'Ã¢â€šÂ¬',
    correct: '€',
    tier: 'hot',
    description: 'Símbolo euro con doble encoding',
    regex: /Ã¢â€šÂ¬/g,
    frequency: 5.3,
    source: 'Presupuestos en documentos legacy',
  },
] as const;

// =====================================================
// PATRONES NUEVOS - TIER WARM (Frecuencia media 1-5%)
// =====================================================

/**
 * 16 patrones WARM nuevos - Frecuencia moderada
 * Incluye caracteres de control invisibles críticos para parsing
 */
export const NEW_WARM_PATTERNS: Readonly<UTF8Pattern[]> = [
  // W01-W06: Caracteres de control invisibles
  {
    corrupted: '\u00A0',
    correct: ' ',
    tier: 'warm',
    description: 'Non-breaking space → espacio normal',
    regex: /\u00A0/g,
    frequency: 4.8,
    source: 'Copiar/pegar desde Word, PDFs',
  },
  {
    corrupted: '\u200B',
    correct: '',
    tier: 'warm',
    description: 'Zero-width space → eliminar',
    regex: /\u200B/g,
    frequency: 3.2,
    source: 'Editores HTML, copy/paste web',
  },
  {
    corrupted: '\u200C',
    correct: '',
    tier: 'warm',
    description: 'Zero-width non-joiner → eliminar',
    regex: /\u200C/g,
    frequency: 1.5,
    source: 'Textos árabes/hindúes mal procesados',
  },
  {
    corrupted: '\u200D',
    correct: '',
    tier: 'warm',
    description: 'Zero-width joiner → eliminar',
    regex: /\u200D/g,
    frequency: 1.4,
    source: 'Emojis descompuestos, textos complejos',
  },
  {
    corrupted: '\uFEFF',
    correct: '',
    tier: 'warm',
    description: 'BOM (Byte Order Mark) → eliminar',
    regex: /\uFEFF/g,
    frequency: 2.9,
    source: 'Inicio de archivos UTF-8 con BOM',
  },
  {
    corrupted: '\u00AD',
    correct: '',
    tier: 'warm',
    description: 'Soft hyphen → eliminar',
    regex: /\u00AD/g,
    frequency: 2.1,
    source: 'Documentos con guiones opcionales',
  },
  // W07-W16: Patrones originales
  {
    corrupted: 'ÃƒÅ"',
    correct: 'Ü',
    tier: 'warm',
    description: 'U con diéresis mayúscula',
    regex: /ÃƒÅ"/g,
    frequency: 3.2,
    source: 'Nombres propios alemanes',
  },
  {
    corrupted: 'Ãƒâ€"',
    correct: 'Ö',
    tier: 'warm',
    description: 'O con diéresis mayúscula',
    regex: /Ãƒâ€"/g,
    frequency: 2.1,
    source: 'Nombres propios',
  },
  {
    corrupted: 'ÃƒÂ§',
    correct: 'ç',
    tier: 'warm',
    description: 'C cedilla minúscula',
    regex: /ÃƒÂ§/g,
    frequency: 4.1,
    source: 'Palabras catalanas/portuguesas',
  },
  {
    corrupted: 'Ãƒâ€¡',
    correct: 'Ç',
    tier: 'warm',
    description: 'C cedilla mayúscula',
    regex: /Ãƒâ€¡/g,
    frequency: 1.8,
    source: 'Inicio de frase catalán',
  },
  {
    corrupted: 'Ã‚Â±',
    correct: '±',
    tier: 'warm',
    description: 'Símbolo más/menos',
    regex: /Ã‚Â±/g,
    frequency: 2.9,
    source: 'Tolerancias "±5m"',
  },
  {
    corrupted: 'Ã‚Â²',
    correct: '²',
    tier: 'warm',
    description: 'Superíndice 2 (cuadrado)',
    regex: /Ã‚Â²/g,
    frequency: 4.5,
    source: 'Superficies "m²"',
  },
  {
    corrupted: 'Ã‚Â³',
    correct: '³',
    tier: 'warm',
    description: 'Superíndice 3 (cúbico)',
    regex: /Ã‚Â³/g,
    frequency: 2.3,
    source: 'Volúmenes "m³"',
  },
  {
    corrupted: 'Ã‚Â¿',
    correct: '¿',
    tier: 'warm',
    description: 'Interrogación invertida',
    regex: /Ã‚Â¿/g,
    frequency: 3.7,
    source: 'Preguntas en español',
  },
  {
    corrupted: 'Ã‚Â¡',
    correct: '¡',
    tier: 'warm',
    description: 'Exclamación invertida',
    regex: /Ã‚Â¡/g,
    frequency: 2.4,
    source: 'Exclamaciones en español',
  },
  {
    corrupted: 'Ã¢â‚¬Å"',
    correct: '"',
    tier: 'warm',
    description: 'Comilla doble apertura',
    regex: /Ã¢â‚¬Å"/g,
    frequency: 3.1,
    source: 'Citas en documentos',
  },
] as const;

// =====================================================
// PATRONES NUEVOS - TIER COLD (Frecuencia baja <1%)
// =====================================================

/**
 * 14 patrones COLD nuevos - Raros pero documentados
 * Incluye griegos, matemáticos y flechas
 */
export const NEW_COLD_PATTERNS: Readonly<UTF8Pattern[]> = [
  // C01-C06: Patrones originales
  {
    corrupted: 'Ã‚Â©',
    correct: '©',
    tier: 'cold',
    description: 'Símbolo copyright',
    regex: /Ã‚Â©/g,
    frequency: 0.8,
    source: 'Pie de documentos',
  },
  {
    corrupted: 'Ã‚Â®',
    correct: '®',
    tier: 'cold',
    description: 'Símbolo marca registrada',
    regex: /Ã‚Â®/g,
    frequency: 0.4,
    source: 'Nombres comerciales',
  },
  {
    corrupted: 'Ã¢â€žÂ¢',
    correct: '™',
    tier: 'cold',
    description: 'Símbolo trademark',
    regex: /Ã¢â€žÂ¢/g,
    frequency: 0.3,
    source: 'Marcas en documentos',
  },
  {
    corrupted: 'Ã‚Â½',
    correct: '½',
    tier: 'cold',
    description: 'Fracción un medio',
    regex: /Ã‚Â½/g,
    frequency: 0.6,
    source: 'Medidas "1½ km"',
  },
  {
    corrupted: 'Ã‚Â¼',
    correct: '¼',
    tier: 'cold',
    description: 'Fracción un cuarto',
    regex: /Ã‚Â¼/g,
    frequency: 0.5,
    source: 'Medidas parciales',
  },
  {
    corrupted: 'Ã¢â‚¬Â¢',
    correct: '•',
    tier: 'cold',
    description: 'Bullet point',
    regex: /Ã¢â‚¬Â¢/g,
    frequency: 0.9,
    source: 'Listas en documentos',
  },
  // C07-C10: Caracteres griegos
  {
    corrupted: 'Î±',
    correct: 'α',
    tier: 'cold',
    description: 'Letra griega alpha',
    regex: /Î±/g,
    frequency: 0.4,
    source: 'Fórmulas técnicas',
  },
  {
    corrupted: 'Î²',
    correct: 'β',
    tier: 'cold',
    description: 'Letra griega beta',
    regex: /Î²/g,
    frequency: 0.3,
    source: 'Fórmulas técnicas',
  },
  {
    corrupted: 'Ï€',
    correct: 'π',
    tier: 'cold',
    description: 'Letra griega pi',
    regex: /Ï€/g,
    frequency: 0.5,
    source: 'Cálculos de círculos',
  },
  {
    corrupted: 'Î©',
    correct: 'Ω',
    tier: 'cold',
    description: 'Letra griega omega',
    regex: /Î©/g,
    frequency: 0.2,
    source: 'Ohmios en documentos técnicos',
  },
  // C11-C12: Símbolos matemáticos
  {
    // UTF-8 de × (C3 97) interpretado como Latin-1 = Ã + byte 0x97
    corrupted: 'Ã\x97',
    correct: '×',
    tier: 'cold',
    description: 'Símbolo multiplicación',
    regex: /Ã\x97/g,
    frequency: 0.7,
    source: 'Dimensiones "3×4 m"',
  },
  {
    corrupted: 'Ã·',
    correct: '÷',
    tier: 'cold',
    description: 'Símbolo división',
    regex: /Ã·/g,
    frequency: 0.2,
    source: 'Fórmulas legacy',
  },
  // C13-C14: Flechas
  // NOTA: Las flechas Unicode tienen mojibake complejo
  // → (U+2192) = UTF-8 E2 86 92 → Latin-1 â\x86\x92
  // ← (U+2190) = UTF-8 E2 86 90 → Latin-1 â\x86\x90
  {
    corrupted: 'â\x86\x92',
    correct: '→',
    tier: 'cold',
    description: 'Flecha derecha',
    regex: /â\x86\x92/g,
    frequency: 0.6,
    source: 'Diagramas de flujo en planes',
  },
  {
    corrupted: 'â\x86\x90',
    correct: '←',
    tier: 'cold',
    description: 'Flecha izquierda',
    regex: /â\x86\x90/g,
    frequency: 0.3,
    source: 'Diagramas de flujo en planes',
  },
] as const;

// =====================================================
// AGREGACIÓN Y EXPORTACIÓN
// =====================================================

/**
 * Todos los patrones nuevos combinados (50 total)
 */
export const ALL_NEW_PATTERNS: Readonly<UTF8Pattern[]> = [
  ...NEW_HOT_PATTERNS,
  ...NEW_WARM_PATTERNS,
  ...NEW_COLD_PATTERNS,
] as const;

/**
 * Obtiene estadísticas de los patrones nuevos
 */
export function getNewPatternStats(): PatternStats {
  return {
    total: ALL_NEW_PATTERNS.length,
    byTier: {
      hot: NEW_HOT_PATTERNS.length,
      warm: NEW_WARM_PATTERNS.length,
      cold: NEW_COLD_PATTERNS.length,
    },
    applications: new Map(),
  };
}

/**
 * Obtiene patrones por tier para procesamiento optimizado
 */
export function getPatternsByTier(tier: 'hot' | 'warm' | 'cold'): UTF8Pattern[] {
  switch (tier) {
    case 'hot':
      return [...NEW_HOT_PATTERNS];
    case 'warm':
      return [...NEW_WARM_PATTERNS];
    case 'cold':
      return [...NEW_COLD_PATTERNS];
  }
}

/**
 * Busca un patrón específico por su texto corrupto
 */
export function findPattern(corrupted: string): UTF8Pattern | undefined {
  return ALL_NEW_PATTERNS.find(p => p.corrupted === corrupted);
}

// =====================================================
// INTEGRACIÓN CON SISTEMA EXISTENTE
// =====================================================

/**
 * Formato para integrar con EncodingCorrector existente
 * Convierte al formato [corrupted, correct] que usa el sistema actual
 */
export function toEncodingCorrectorFormat(): Array<[string, string]> {
  return ALL_NEW_PATTERNS.map(p => [p.corrupted, p.correct]);
}

/**
 * Genera mapa de reemplazo para uso directo
 */
export function toReplacementMap(): Map<string, string> {
  const map = new Map<string, string>();
  for (const pattern of ALL_NEW_PATTERNS) {
    map.set(pattern.corrupted, pattern.correct);
  }
  return map;
}

/**
 * Exporta patrones ordenados por frecuencia (mayor primero)
 */
export function getPatternsByFrequency(): UTF8Pattern[] {
  return [...ALL_NEW_PATTERNS].sort(
    (a, b) => (b.frequency ?? 0) - (a.frequency ?? 0)
  );
}
